import { ProductDesignPageHeader, ProductDesignSection } from './ProductDesignDocPrimitives';
import { DataTable, Callout, type Column } from '../../components/ds';
import { GAP_SCALE, PADDING_SCALE, WIDTH_USAGE, GRID_COLS, MEASURED_ON, type ScaleStep } from './designUsage.generated';

/**
 * Layout & grid — the spec the register flagged as missing.
 *
 * The previous page was staged prose about "grid rhythm (typically 16px
 * multiples)". The app does have a spacing scale; it just was not written
 * down, so it was being rediscovered per screen. Page width has no convention
 * at all, which is the more consequential finding and the one this page rules
 * on.
 */

const SPACING_ROLES: Record<string, string> = {
  '0.5': 'Icon-to-label inside a chip. Below this, nothing.',
  '1': 'Tight inline pairs — a glyph and its count.',
  '1.5': 'Icon and label inside a button or tab.',
  '2': 'The default. Adjacent controls, items in a row, list rows.',
  '2.5': 'Off-scale. Use 2 or 3.',
  '3': 'Between distinct controls, or a label and its field.',
  '4': 'Between blocks inside a panel; standard card padding.',
  '5': 'Off-scale. Use 4 or 6.',
  '6': 'Between panels; padding for a large panel.',
  '8': 'Between major page sections.',
};

const OFF_SCALE = new Set(['2.5', '5']);

const scaleColumns = (roles: Record<string, string>): Column<ScaleStep>[] => [
  {
    key: 'step',
    header: 'Step',
    width: '12%',
    render: (r) => (
      <span className={`font-mono ${OFF_SCALE.has(r.step) ? 'text-[var(--coral)]' : 'text-[var(--text-pri)]'}`}>
        {r.step}
      </span>
    ),
  },
  {
    key: 'px',
    header: 'Pixels',
    width: '12%',
    numeric: true,
    render: (r) => `${parseFloat(r.step) * 4}px`,
  },
  { key: 'count', header: 'Uses', width: '12%', numeric: true, render: (r) => r.count.toLocaleString() },
  { key: 'role', header: 'What it is for', render: (r) => roles[r.step] ?? '—' },
];

export const PrioritiesAILayoutGridPage: React.FC = () => {
  const widthTotal = WIDTH_USAGE.reduce((n, w) => n + w.count, 0);
  const topWidth = [...WIDTH_USAGE].sort((a, b) => b.count - a.count)[0];

  return (
    <>
      <ProductDesignPageHeader
        title="Layout & grid"
        eyebrow="Foundations · rhythm"
        subtitle={
          <>
            A 4px base scale, which the app had already converged on without anyone writing it down —
            and a page-width convention, which it had not. Counts are generated from a scan of{' '}
            <code className="font-mono text-[0.85em] text-[var(--mustard)]">src/</code>, measured {MEASURED_ON}.
          </>
        }
      />

      <ProductDesignSection
        title="Spacing scale"
        hint="Base 4px. Every step below is one the app already uses at volume — this is a description that became a rule, not a rule imposed on the code."
      >
        <DataTable
          columns={scaleColumns(SPACING_ROLES)}
          rows={GAP_SCALE}
          getRowKey={(r) => r.step}
          density="compact"
          caption="Gap scale"
          captionHidden
        />
        <p className="mt-4 text-sm leading-relaxed text-[var(--text-sec)]">
          The two coral steps are off-scale. They exist in small numbers and are almost always a nudge
          to compensate for something else being off-system — a control at a custom height, a border
          that should have been a shadow. Fix the cause rather than the gap.
        </p>
      </ProductDesignSection>

      <ProductDesignSection
        title="Container padding"
        hint="Same ladder. p-4 for a standard card, p-6 for a large panel, p-3 for a dense row."
      >
        <DataTable
          columns={scaleColumns({
            '0.5': 'Icon button hit area only.',
            '1': 'Dense chip.',
            '1.5': 'Small control.',
            '2': 'Compact row or cell.',
            '2.5': 'Off-scale. Use 2 or 3.',
            '3': 'Dense panel, list row.',
            '4': 'Standard card and panel body.',
            '5': 'Off-scale. Use 4 or 6.',
            '6': 'Large panel, page section.',
            '8': 'Full-page container.',
          })}
          rows={PADDING_SCALE}
          getRowKey={(r) => r.step}
          density="compact"
          caption="Padding scale"
          captionHidden
        />
      </ProductDesignSection>

      <ProductDesignSection
        title="Page width — the actual gap"
        hint="This is where there is no convention, and it is why pages that should feel like siblings do not."
      >
        <Callout tone="coral" label="The finding" title="Eleven different max-widths, none dominant">
          {widthTotal.toLocaleString()} uses spread across every step Tailwind offers, the largest
          being <code className="font-mono text-[0.85em]">max-w-{topWidth.step}</code> at just{' '}
          {topWidth.count}. A page's width is currently whatever its author picked, which is why two
          pages doing the same job can read as different products.
        </Callout>

        <div className="mt-5">
          <DataTable
            columns={[
              {
                key: 'step',
                header: 'Width',
                width: '18%',
                render: (r: ScaleStep) => <span className="font-mono text-[var(--mustard)]">max-w-{r.step}</span>,
              },
              { key: 'count', header: 'Uses', width: '12%', numeric: true, render: (r: ScaleStep) => r.count.toLocaleString() },
              {
                key: 'role',
                header: 'Use it for',
                render: (r: ScaleStep) =>
                  ({
                    xs: 'Nothing at page level. A narrow inline element only.',
                    sm: 'A single form field or a short empty state.',
                    md: 'A dialog body.',
                    lg: 'A standard dialog.',
                    xl: 'A wide dialog.',
                    '2xl': 'A single-column reading page — a brief, a decision record.',
                    '3xl': 'A reading page with a sidebar.',
                    '4xl': 'A form-heavy configuration page.',
                    '5xl': 'The Elevation page shell — the default for a content page.',
                    '6xl': 'A dense working surface: catalogs, multi-column dashboards.',
                    '7xl': 'A full-bleed data surface. Needs a reason.',
                  }[r.step] ?? '—'),
              },
            ]}
            rows={WIDTH_USAGE}
            getRowKey={(r) => r.step}
            density="compact"
            caption="Page width by role"
            captionHidden
          />
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[var(--text-sec)]">
          The rule: pick the width from the page's <em>job</em>, not from how much content it happens
          to have today. <code className="font-mono text-[0.85em] text-[var(--mustard)]">PageShell</code>{' '}
          in <code className="font-mono text-[0.85em]">@/components/elevation</code> already fixes{' '}
          <code className="font-mono text-[0.85em]">max-w-5xl</code> for Elevation pages; product
          pages have no equivalent, which is the concrete thing to fix next.
        </p>
      </ProductDesignSection>

      <ProductDesignSection
        title="Columns"
        hint="One to four. Five and above appear only where a grid is standing in for a table — reach for DataTable instead."
      >
        <DataTable
          columns={[
            { key: 'step', header: 'Columns', width: '18%', render: (r: ScaleStep) => (
              <span className={`font-mono ${Number(r.step) > 4 ? 'text-[var(--coral)]' : 'text-[var(--text-pri)]'}`}>
                grid-cols-{r.step}
              </span>
            ) },
            { key: 'count', header: 'Uses', width: '14%', numeric: true, render: (r: ScaleStep) => r.count.toLocaleString() },
            { key: 'role', header: 'Note', render: (r: ScaleStep) =>
              Number(r.step) > 4
                ? 'A grid this wide is almost always a table wearing a costume — it loses the header, the sort, and the screen-reader row/column relationship.'
                : 'Standard.' },
          ]}
          rows={GRID_COLS}
          getRowKey={(r) => r.step}
          density="compact"
          caption="Grid column counts"
          captionHidden
        />
      </ProductDesignSection>
    </>
  );
};
